import { useCallback, useEffect, useState } from 'react';
import { useIsDesktop } from '@/core/hooks/useMediaQuery';
import { cn } from '@/shared/utils/cn';
import type { CustomizationService } from '../../application/services/CustomizationService';
import type { SizeOptionWithEdd } from '../../domain/models/CustomizationOptions';
import type { SizeOption } from '../../utils/parseSizeList';
import type { ProductCustomization } from '../../domain/models/ProductCustomization';
import {
  getDesktopSizerLinkLabel,
  getSizeSectionLabel,
  getSizeSelectPlaceholder,
} from '../../utils/sizeSection.utils';
import {
  getSizerLinkLabel,
  isSizerLinkVisible,
  shouldShowChainSizerLink,
  shouldShowRingSizerLink,
} from '@/features/sizer/utils/sizerVisibility.utils';
import {
  shouldShowPdpFreeSize,
  shouldShowPdpSizeDropdown,
} from '../../utils/shouldShowPdpSizeSection';
import { useSizeSelection, type SizeSelectionContext } from '../hooks/useSizeSelection';
import { useSizerEnabled } from '@/features/sizer/presentation/hooks/useSizerEnabled';
import { CustomizationDesktopSizeSection } from './CustomizationDesktopSizeSection';
import { CustomizationSizeSheet } from './CustomizationSizeSheet';
import { ChainLengthGuideSheet } from '@/features/sizer/presentation/components/ChainLengthGuideSheet';
import { RingSizerSheet } from '@/features/sizer/presentation/components/RingSizerSheet';
import type { SizerService } from '@/features/sizer/application/services/SizerService';

export interface CustomizationSizeSectionProps {
  service: CustomizationService;
  sizerService: SizerService | null;
  context: SizeSelectionContext | null;
  sizes: SizeOptionWithEdd[];
  selectedSize: SizeOption | null;
  category: string;
  productSize: string | null;
  isSizeable: boolean;
  nddAvailableSizes: string[] | null;
  readyToShipQuantity: number | null;
  hasPincode: boolean;
  customizationId: number;
  onApplied: (result: {
    customization: ProductCustomization;
    selectedSize: SizeOption | null;
  }) => void;
  onRegisterValidate?: (validate: () => boolean) => void;
  onSizeSelected?: (sizeDisplay: string) => void;
  onFreeSizerClick?: () => void;
  onFreeSizerDetailsAdded?: () => void;
  onResendOtp?: () => void;
  onOtpVerified?: () => void;
}

export function CustomizationSizeSection({
  service,
  sizerService,
  context,
  sizes,
  selectedSize,
  category,
  productSize,
  isSizeable,
  nddAvailableSizes,
  readyToShipQuantity,
  hasPincode,
  customizationId,
  onApplied,
  onRegisterValidate,
  onSizeSelected,
  onFreeSizerClick,
  onFreeSizerDetailsAdded,
  onResendOtp,
  onOtpVerified,
}: CustomizationSizeSectionProps) {
  const [isSizeSheetOpen, setIsSizeSheetOpen] = useState(false);
  const [activeSizerSheet, setActiveSizerSheet] = useState<'ring' | 'chain' | null>(null);
  const isDesktop = useIsDesktop();

  const { isSizerEnabled } = useSizerEnabled(sizerService);

  const { isApplying, sizeNotSelected, shakeKey, handleSizeSelect, validateSizeSelection } = useSizeSelection({
    service,
    context,
    selectedSize,
    isSizeable,
    readyToShipQuantity,
    onApplied,
  });

  useEffect(() => {
    onRegisterValidate?.(validateSizeSelection);
  }, [onRegisterValidate, validateSizeSelection]);

  const sizeLabel = getSizeSectionLabel(category);
  const placeholder = getSizeSelectPlaceholder(category);

  const visibilityInput = {
    productSize,
    isSizeableProduct: isSizeable,
    sizeListLength: sizes.length,
    readyToShipQuantity,
  };
  const showDropdown = shouldShowPdpSizeDropdown(visibilityInput);
  const showFreeSize = shouldShowPdpFreeSize(visibilityInput);
  const showSizerLink = isSizerLinkVisible(category, isSizerEnabled, showDropdown);

  const handleOpenSizeSheet = useCallback(() => {
    if (showDropdown) setIsSizeSheetOpen(true);
  }, [showDropdown]);

  const handleCloseSizeSheet = useCallback(() => setIsSizeSheetOpen(false), []);

  const handleSelectSize = useCallback(
    async (size: SizeOptionWithEdd) => {
      await handleSizeSelect(size);
      onSizeSelected?.(size.value);
      setIsSizeSheetOpen(false);
    },
    [handleSizeSelect, onSizeSelected]
  );

  const handleOpenSizer = useCallback(() => {
    onFreeSizerClick?.();
    if (shouldShowChainSizerLink(category)) {
      setActiveSizerSheet('chain');
    } else if (shouldShowRingSizerLink(category)) {
      setActiveSizerSheet('ring');
    }
  }, [category, onFreeSizerClick]);

  if (!showDropdown && !showFreeSize) {
    return null;
  }

  if (showFreeSize) {
    return (
      <div id="pdp-size-section" className="px-[15px] py-[15px] md:px-0">
        <h3 className="text-sm font-bold leading-5 text-black">Size</h3>
        <p className="mt-2 text-[13px] leading-snug text-[#666]">Free Size</p>
      </div>
    );
  }

  if (isDesktop) {
    return (
      <>
        <CustomizationDesktopSizeSection
          sizeLabel={sizeLabel}
          placeholder={placeholder}
          sizes={sizes}
          selectedSize={selectedSize}
          hasPincode={hasPincode}
          nddAvailableSizes={nddAvailableSizes}
          isApplying={isApplying}
          sizeNotSelected={sizeNotSelected}
          shakeKey={shakeKey}
          sizerLinkLabel={showSizerLink ? getDesktopSizerLinkLabel(category) : null}
          onOpenSizer={handleOpenSizer}
          onSelect={handleSelectSize}
        />

        {sizerService && activeSizerSheet === 'ring' && (
          <RingSizerSheet
            open
            sizerService={sizerService}
            customizationId={customizationId}
            category={category}
            onClose={() => setActiveSizerSheet(null)}
            onFreeSizerDetailsAdded={onFreeSizerDetailsAdded}
            onResendOtp={onResendOtp}
            onOtpVerified={onOtpVerified}
          />
        )}

        {activeSizerSheet === 'chain' && (
          <ChainLengthGuideSheet
            open
            onClose={() => setActiveSizerSheet(null)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div id="pdp-size-section" className="px-[15px] pb-5 pt-[15px] md:px-0">
        <div className="mb-2.5 flex items-center justify-between gap-3">
          <h3
            key={shakeKey}
            className={cn(
              'text-sm font-bold leading-5 text-black',
              sizeNotSelected && 'text-red-600',
              shakeKey > 0 && 'animate-shake'
            )}
          >
            {sizeLabel}
          </h3>
          {showSizerLink && (
            <button
              type="button"
              className="shrink-0 border-0 bg-transparent p-0 text-[13px] leading-5 text-[var(--link-blue)] hover:underline"
              onClick={handleOpenSizer}
            >
              {getSizerLinkLabel(category)}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={handleOpenSizeSheet}
          disabled={isApplying}
          className={cn(
            'relative flex w-full items-center border border-[#d9d9d9] bg-[#f9f9f9] px-2.5 py-2.5 text-left text-[13px] leading-snug',
            selectedSize ? 'text-black' : 'text-black',
            sizeNotSelected && 'border-red-500'
          )}
        >
          <span className="pr-6">{selectedSize?.value ?? `Select ${placeholder}`}</span>
          <span
            className="pointer-events-none absolute right-[15px] top-1/2 -mt-0.5 h-0 w-0 border-x-4 border-x-transparent border-t-4 border-t-black"
            aria-hidden
          />
        </button>
      </div>

      <CustomizationSizeSheet
        open={isSizeSheetOpen}
        category={category}
        sizes={sizes}
        selectedSize={selectedSize}
        nddAvailableSizes={nddAvailableSizes}
        hasPincode={hasPincode}
        isApplying={isApplying}
        onClose={handleCloseSizeSheet}
        onSelect={handleSelectSize}
      />

      {sizerService && activeSizerSheet === 'ring' && (
        <RingSizerSheet
          open
          sizerService={sizerService}
          customizationId={customizationId}
          category={category}
          onClose={() => setActiveSizerSheet(null)}
          onFreeSizerDetailsAdded={onFreeSizerDetailsAdded}
          onResendOtp={onResendOtp}
          onOtpVerified={onOtpVerified}
        />
      )}

      {activeSizerSheet === 'chain' && (
        <ChainLengthGuideSheet
          open
          onClose={() => setActiveSizerSheet(null)}
        />
      )}
    </>
  );
}
